import { useState } from "react";
import { Folder, FolderOpen, ChevronRight, ChevronDown } from "lucide-react";
import type { FolderNode } from "../types";

interface FolderTreeProps {
  folders: FolderNode[];
  selectedPath: string | null;
  onSelect: (path: string | null) => void;
}

function FolderItem({
  node,
  selectedPath,
  onSelect,
  depth = 0,
}: {
  node: FolderNode;
  selectedPath: string | null;
  onSelect: (path: string | null) => void;
  depth?: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedPath === node.path;
  const isParentOfSelected = selectedPath?.startsWith(node.path + "/") ?? false;

  const handleClick = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    }
    onSelect(isSelected ? null : node.path);
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className={`flex items-center gap-1.5 w-full px-2 py-1 text-sm rounded-md transition-colors text-left ${
          isSelected
            ? "bg-neutral-100 text-[#0000ff]"
            : isParentOfSelected
              ? "text-neutral-700"
              : "text-neutral-600 hover:bg-neutral-50"
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {hasChildren ? (
          expanded ? (
            <ChevronDown className="w-3.5 h-3.5 shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 shrink-0" />
          )
        ) : (
          <span className="w-3.5" />
        )}
        {isSelected || isParentOfSelected ? (
          <FolderOpen className="w-4 h-4 shrink-0" />
        ) : (
          <Folder className="w-4 h-4 shrink-0" />
        )}
        <span className="truncate">{node.label}</span>
      </button>
      {hasChildren && expanded && (
        <div>
          {node.children!.map((child) => (
            <FolderItem
              key={child.id}
              node={child}
              selectedPath={selectedPath}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function FolderTree({ folders, selectedPath, onSelect }: FolderTreeProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <button
        onClick={() => onSelect(null)}
        className={`flex items-center gap-1.5 px-2 py-1 text-sm rounded-md transition-colors ${
          selectedPath === null
            ? "bg-neutral-100 text-[#0000ff] font-medium"
            : "text-neutral-600 hover:bg-neutral-50"
        }`}
      >
        <FolderOpen className="w-4 h-4" />
        All Files
      </button>
      {folders.map((node) => (
        <FolderItem
          key={node.id}
          node={node}
          selectedPath={selectedPath}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
